import { Typography } from '@mui/material';

import React, { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { generateEllipsisTooltip } from 'common/components/Tooltip/EllipsisTooltip';
import { PAGINATION_CONSTANTS } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { DATA_ACCESS_PERMISSIONS_ACTIONS } from 'common/constants/PermissionsConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setPagination,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setGroupDetails,
	setGroupPermissionDrawer,
} from 'common/store/reducer/permissionsReducer';

import { getGroupById, deleteGroupById } from '../api';
import { IGroups } from '../api/type-def';
import { generateCommonColumns } from '../commonColumnsUtils';

import styles from './GroupsTableCmp.module.scss';

interface GroupsTableProps {
	groupsData: IGroups[];
	isLoading: boolean;
	totalPagesCount: number;
	totalRecordsCount: number;
	fetchGroupsTableData: any;
}

const { OK } = HTTP_STATUS_CODES;

const { EDITLABEL } = DATA_ACCESS_PERMISSIONS_ACTIONS;

const GroupsTable: React.FC<GroupsTableProps> = ({
	groupsData,
	isLoading,
	totalPagesCount,
	totalRecordsCount,
	fetchGroupsTableData,
}: GroupsTableProps) => {
	const dispatch = useDispatch();

	const [, setGroupId] = useState<string>('');
	const [data, setGroupData] = useState<any>();

	const openCreateGroupDrawer = useCallback(() => {
		dispatch(setGroupPermissionDrawer(true));
	}, [data]);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const onActionClick = async (params: any, type: string) => {
		setGroupId(params?.groupId);
		if (type !== EDITLABEL) {
			const buildPayLoadObject = {
				sortKey: 'groupName',
				pageSize: PAGINATION_CONSTANTS.PAGE_SIZE,
				pageNumber: PAGINATION_CONSTANTS.PAGE_NUMBER,
				sortDirection: 'desc',
			};
			const groupDataResponse = await deleteGroupById(params, params?.groupId);
			if (groupDataResponse.statusCode === OK) {
				fetchGroupsTableData(buildPayLoadObject);
				dispatch(
					setToastMessage({
						message: 'Group Deleted Successfully.',
						severity: 'success',
						isToastOpen: true,
					}),
				);
			} else {
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		} else {
			const groupDataResponse = await getGroupById(params?.groupId);
			if (groupDataResponse?.statusCode === OK) {
				openCreateGroupDrawer();
				setGroupData(groupDataResponse.responseBody);
				dispatch(
					setGroupDetails({
						type: EDITLABEL,
						data: groupDataResponse.responseBody,
					}),
				);
			} else {
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		}
	};

	const columns: readonly Column[] = [
		{
			id: 'groupName',
			label: 'Groups',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography
					component="div"
					className={`${styles['groups-table-groups-name']}`}
				>
					{params?.groupName}
				</Typography>
			),
		},
		{
			id: 'roles',
			label: 'Roles',
			disablePadding: false,
			isSortable: false,
			render: (params: any) =>
				generateEllipsisTooltip({
					items: params?.roles.map((role: any) => ({ name: role.roleName })),
					maxVisibleItems: 2,
					dataTestId: 'roles-tooltip',
				}),
		},

		...generateCommonColumns(onActionClick),
	];
	const handlePageChange = (data: any) => {
		dispatch(setPagination(data));
		fetchGroupsTableData(data);
	};

	const handleSortChange = (data: any) => {
		dispatch(setPagination(data));
		fetchGroupsTableData(data);
	};
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);
	return (
		<div className={styles['groups-table-container']}>
			<TableComponent
				rows={groupsData}
				columns={columns}
				totalItems={totalRecordsCount}
				defaultSortKey=""
				onPageHandler={handlePageChange}
				isLoading={isLoading}
				tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
				totalPagesCount={totalPagesCount}
				onSortHandler={handleSortChange}
				enableHeaderColor
				isRenderPagination
			/>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</div>
	);
};

export default GroupsTable;
